import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { LoaderComponent } from 'shared/LoaderComponent';
import { usePracticeAreasQuery } from 'hooks/queries/usePracticeAreasQuery';
import debounce from 'lodash.debounce';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';
import { Button } from 'primereact/button';

/**
 * PaymentRequestsFiltersV2 Component
 * Filter component for Payment Requests V2 with search, date range, and practice area selection
 *
 * @param {Object} props
 * @param {string} props.searchKey - Current search keyword
 * @param {Function} props.onChangeSearch - Handler for search input changes
 * @param {Function} props.onSearchKeyDown - Handler for search keydown (Enter key)
 * @param {''|Date|Array|null} props.dateFilter - Range selection or '' when cleared
 * @param {number} props.dateFilterResetKey - Increments on reset to remount Calendar (PrimeReact range fix)
 * @param {Function} props.onChangeCalendar - Handler for date filter changes
 * @param {Array|null} props.selectedPractice - Selected practice area IDs
 * @param {Function} props.onChangePractice - Handler for practice area changes
 * @param {Function} props.refreshData - Handler for resetting filters
 * @param {boolean} props.isFromCaseSpecificModule - Whether in case-specific module
 * @param {boolean} props.isLeadSpecific - Whether in leads module
 */
const PaymentRequestsFiltersV2 = ({
  searchKey,
  onChangeSearch,
  onSearchKeyDown,
  dateFilter,
  dateFilterResetKey = 0,
  onChangeCalendar,
  selectedPractice,
  onChangePractice,
  refreshData,
  isFromCaseSpecificModule,
  isLeadSpecific,
}) => {
  // Local state for search input (for immediate UI updates)
  const [localSearchKey, setLocalSearchKey] = useState(searchKey || '');

  // Sync local state with prop when prop changes externally (e.g., from refresh)
  useEffect(() => {
    setLocalSearchKey(searchKey || '');
  }, [searchKey]);

  // Create debounced search function
  const debouncedSearchRef = useRef(null);

  useEffect(() => {
    // Create debounced function that calls the parent's onChangeSearch
    debouncedSearchRef.current = debounce((value) => {
      if (onChangeSearch) {
        // Create a synthetic event object to match the expected signature
        onChangeSearch({ target: { value } });
      }
    }, 500); // 500ms delay

    // Cleanup on unmount
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [onChangeSearch]);

  // Use React Query hook for practice areas with dynamic query parameters
  const { practiceAreaList, isPracticeAreaLoading } = usePracticeAreasQuery({
    loadFullList: true,
    status: 'active',
    excludeFromLoading: false,
    sort_order: 'alphabet_ascending',
    pagination_token: '',
    pagination_direction: 'forward',
  });

  // Map practice areas to dropdown options
  const practiceAreaOptions = useMemo(() => {
    if (practiceAreaList && practiceAreaList.length > 0) {
      return practiceAreaList.map((val) => ({
        label: val.practice_area_name,
        value: val.practice_area_id,
      }));
    }
    return [];
  }, [practiceAreaList]);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchKey(value); // Update local state immediately for UI responsiveness
    // Debounce the API call - will trigger after user stops typing for 500ms
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current(value);
    }
  };

  // Handle search keydown (Enter key)
  const handleSearchKeyDown = (e) => {
    const code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      // Enter key pressed - cancel debounce and trigger immediately
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
      if (onSearchKeyDown) {
        onSearchKeyDown(e);
      }
    }
  };
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="">
      <div className="d-flex flex-wrap align-items-center filter-wrap p-lg-0 p-2 mb-3 w-100 align-items-center justify-content-between">
        <div className="p-2 filter-wraper">
          <span className="p-input-icon-left input-shadow">
            <i className="icon-search F-size16"></i>
            <TextSnippetWrapper
              type="text"
              placeholder="Search"
              onChange={handleSearchChange}
              value={localSearchKey}
              onKeyDown={handleSearchKeyDown}
              className="p-inputtext p-component input-search"
              autoComplete="off"
            />
          </span>
        </div>
        <div className="d-block d-lg-none w-100">
          <Button
            className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            aria-controls="payment-filter-section"
            label="Filters"
          >
            <span
              style={{
                fontSize: 18,
                transition: 'transform 0.2s',
                display: 'inline-block',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <i className="fa fa-solid fa-angle-down"></i>
            </span>
          </Button>
        </div>
        <div
          className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
          id="payment-filter-section"
          aria-hidden={!showFilters}
        >
          <label className="text-bold black-600 p-0 filter-label">Filters:</label>
          <div className="d-flex flex-column filter-wraper">
            <Calendar
              key={`payment-request-date-${dateFilterResetKey}`}
              className="input-shadow filter-calender"
              selectionMode="range"
              id="icon"
              value={dateFilter === '' || dateFilter == null ? null : dateFilter}
              onChange={onChangeCalendar}
              showIcon
              placeholder="Date"
            />
          </div>
          {!isFromCaseSpecificModule && !isLeadSpecific && (
            <div className="d-flex flex-column mx-0 filter-wraper">
              <MultiSelect
                value={selectedPractice}
                options={practiceAreaOptions}
                onChange={onChangePractice}
                placeholder="Practice Area"
                maxSelectedLabels={1}
                display="chip"
                filter
                className="input-shadow w-100 F-size14 input-width"
                panelFooterTemplate={isPracticeAreaLoading ? LoaderComponent : null}
              />
            </div>
          )}

          <div className="d-flex flex-column mx-2">
            <div className="pointer ms-0">
              <i className="fas fa-sync-alt" onClick={refreshData}></i>
              <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestsFiltersV2;
